import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { api, fmtUSD, formatApiError } from "../lib/api";
import { searchAddresses } from "../lib/photonAutocomplete";
import { Banknote, Copy, CheckCircle2, CreditCard, X, UserPlus, LogIn, Landmark } from "lucide-react";
import { toast } from "sonner";
import StripeInlinePayment from "../components/StripeInlinePayment";

const PAYMENT_METHODS = [
    { id: "card",  label: "Card / Apple Pay / Google Pay", icon: CreditCard, desc: "Enter your card details on this page — no redirect. 3% processing fee applies." },
    { id: "zelle", label: "Zelle", icon: Banknote, desc: "Bank-to-bank instant transfer (US only)." },
    { id: "wire",  label: "Bank Wire Transfer", icon: Landmark, desc: "Domestic wire to our US business bank. Standard for institutional buyers." },
];

export default function CheckoutPage() {
    const { items, clear } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [showAuthPrompt, setShowAuthPrompt] = useState(false);
    const [shipping, setShipping] = useState({
        shipping_name: user?.name || "",
        shipping_address: "",
        shipping_address2: "",
        shipping_city: "",
        shipping_state: "",
        shipping_zip: "",
        shipping_country: "US",
        phone: "",
    });
    const [payment, setPayment] = useState("zelle");
    const [coupon, setCoupon] = useState("");
    const [couponApplied, setCouponApplied] = useState("");  // Only this gets sent to the API
    const [notes, setNotes] = useState("");
    const [placing, setPlacing] = useState(false);
    const [order, setOrder] = useState(null);
    const [config, setConfig] = useState({});
    const [cardClientSecret, setCardClientSecret] = useState(null);
    const [cardOrderNumber, setCardOrderNumber] = useState("");

    useEffect(() => {
        if (items.length === 0) return;
        const payload = {
            items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
            ...shipping,
            payment_method: payment,
            coupon_code: couponApplied || null,
            notes,
        };
        api.post("/checkout/quote", payload)
            .then((r) => setQuote(r.data))
            .catch((e) => {
                if (couponApplied && /Coupon/i.test(formatApiError(e))) {
                    toast.error(formatApiError(e));
                    setCouponApplied("");  // clear the *applied* coupon, not the input
                }
            });
    }, [items, payment, couponApplied, shipping, notes]);

    useEffect(() => {
        api.get("/config/payment").then((r) => setConfig(r.data));
    }, []);

    if (items.length === 0 && !order) {
        return (
            <div className="pt-52 text-center bg-[var(--bg)] min-h-screen">
                <p className="text-secondary">Your cart is empty.</p>
                <button onClick={() => navigate("/shop")} className="btn btn-gold mt-6">Browse Catalog</button>
            </div>
        );
    }

    const placeOrder = async () => {
        setPlacing(true);
        try {
            const { data } = await api.post("/orders", {
                items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
                ...shipping,
                payment_method: payment,
                coupon_code: coupon || null,
                notes,
            });
            // For credit/debit card via Stripe embedded checkout: mount the modal
            // on this page — NO redirect off-site. Stripe returns to /order/{id}
            // (top-frame redirect) once the payment is settled.
            if (payment === "card" && data.stripe_client_secret) {
                setCardClientSecret(data.stripe_client_secret);
                setCardOrderNumber(data.order_number || "");
                return;
            }
            setOrder(data);
            clear();
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setPlacing(false); }
    };

    const submit = async () => {
        for (const k of ["shipping_name", "shipping_address", "shipping_city", "shipping_state", "shipping_zip"]) {
            if (!shipping[k]) { toast.error("Please complete the shipping address."); return; }
        }
        // All orders require an account — show the inline sign-up / sign-in popup.
        if (!user) {
            setShowAuthPrompt(true);
            return;
        }
        await placeOrder();
    };

    if (order) {
        return <ConfirmationPanel order={order} config={config} />;
    }

    return (
        <div data-testid="checkout-page" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-6xl mx-auto px-6 lg:px-10">
                <div className="mb-10">
                    <div className="data-label text-brand mb-3">CHECKOUT</div>
                    <h1 className="font-serif text-5xl md:text-6xl">Place Research Order</h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7 space-y-10">
                        <section>
                            <h2 className="font-serif text-2xl mb-5">Shipping</h2>
                            <form
                                data-testid="shipping-form"
                                autoComplete="on"
                                onSubmit={(e) => e.preventDefault()}
                                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                            >
                                <Field label="Full Name" value={shipping.shipping_name} onChange={(v) => setShipping({ ...shipping, shipping_name: v })} testid="ship-name" autoComplete="shipping name" />
                                <Field label="Phone" value={shipping.phone} onChange={(v) => setShipping({ ...shipping, phone: v })} testid="ship-phone" autoComplete="shipping tel" type="tel" inputMode="tel" />
                                <StreetAddressField
                                    value={shipping.shipping_address}
                                    onChange={(v) => setShipping({ ...shipping, shipping_address: v })}
                                    onPlaceSelected={(parsed) => setShipping((s) => ({
                                        ...s,
                                        shipping_address: parsed.street || s.shipping_address,
                                        shipping_city: parsed.city || s.shipping_city,
                                        shipping_state: parsed.stateCode || parsed.state || s.shipping_state,
                                        shipping_zip: parsed.zip || s.shipping_zip,
                                        shipping_country: parsed.country || s.shipping_country,
                                    }))}
                                />
                                <Field label="Apartment, Suite, Unit, Building, etc. (Optional)" value={shipping.shipping_address2} onChange={(v) => setShipping({ ...shipping, shipping_address2: v })} className="sm:col-span-2" testid="ship-address2" autoComplete="shipping address-line2" />
                                <Field label="City" value={shipping.shipping_city} onChange={(v) => setShipping({ ...shipping, shipping_city: v })} testid="ship-city" autoComplete="shipping address-level2" />
                                <Field label="State" value={shipping.shipping_state} onChange={(v) => setShipping({ ...shipping, shipping_state: v })} testid="ship-state" autoComplete="shipping address-level1" />
                                <Field label="ZIP / Postal" value={shipping.shipping_zip} onChange={(v) => setShipping({ ...shipping, shipping_zip: v })} testid="ship-zip" autoComplete="shipping postal-code" inputMode="numeric" />
                                <Field label="Country" value={shipping.shipping_country} onChange={(v) => setShipping({ ...shipping, shipping_country: v })} testid="ship-country" autoComplete="shipping country-name" />
                            </form>
                        </section>

                        <section>
                            {!cardClientSecret && <h2 className="font-serif text-2xl mb-5">Payment Method</h2>}
                            {cardClientSecret ? (
                                <StripeInlinePayment
                                    publishableKey={config.stripe_publishable_key}
                                    clientSecret={cardClientSecret}
                                    onBack={() => { setCardClientSecret(null); setCardOrderNumber(""); setPlacing(false); }}
                                />
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {PAYMENT_METHODS.map((m) => {
                                            const Icon = m.icon;
                                            const active = payment === m.id;
                                            return (
                                                <button
                                                    key={m.id}
                                                    data-testid={`pay-${m.id}`}
                                                    onClick={() => setPayment(m.id)}
                                                    className={`p-4 border text-left transition-all ${active ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--border)] hover:border-[var(--border-light)]"}`}
                                                >
                                                    <Icon size={18} className={active ? "text-brand mb-2" : "text-secondary mb-2"} />
                                                    <div className="font-mono text-sm">{m.label}</div>
                                                    <div className="text-xs text-muted mt-1">{m.desc}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="data-label mt-4 text-[0.6rem]">Payment-before-ship policy · Order status updates once your transaction clears.</p>
                                </>
                            )}
                        </section>

                        <section>
                            <h2 className="font-serif text-2xl mb-5">Coupon Code</h2>
                            <div className="flex gap-2">
                                <input
                                    value={coupon}
                                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                                    data-testid="coupon-input"
                                    placeholder="WELCOME10"
                                    className="input flex-1"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const code = coupon.trim();
                                        if (!code) return;
                                        setCouponApplied(code);
                                    }}
                                    disabled={!coupon.trim() || couponApplied === coupon.trim()}
                                    className="btn"
                                    data-testid="coupon-apply-btn"
                                >
                                    {couponApplied && couponApplied === coupon.trim() ? "Applied" : "Apply"}
                                </button>
                            </div>
                            {!user && <p className="data-label text-[0.6rem] mt-2">Sign in to use WELCOME10 (10% off your first order)</p>}
                            {user && !user.first_order_discount_used && !couponApplied && (
                                <p className="data-label text-[0.6rem] mt-2 text-[var(--brand)]">First-order buyers: enter WELCOME10 for 10% off</p>
                            )}
                        </section>

                        <section>
                            <h2 className="font-serif text-2xl mb-5">Order Notes <span className="data-label">(Optional)</span></h2>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="notes-input" rows={3} placeholder="Lab name, PO number, special handling…" className="input resize-none" />
                        </section>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="card p-6 lg:sticky lg:top-32 space-y-4">
                            <div className="data-label text-brand">ORDER SUMMARY</div>
                            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                                {items.map((it) => (
                                    <div key={it.id} className="flex items-center gap-3">
                                        <img src={it.image} alt="" className="w-14 h-14 object-cover bg-[var(--bg)]" />
                                        <div className="flex-1">
                                            <div className="font-mono text-xs text-primary">{it.name} × {it.quantity}</div>
                                            <div className="data-label">{it.strength}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="hairline" />
                            {quote && (
                                <div className="space-y-2 font-mono text-sm">
                                    <Row label="Items" value={fmtUSD(quote.subtotal_pre_bulk)} />
                                    {quote.bulk_discount_total > 0 && (
                                        <Row label={`Bulk Discount`} value={`− ${fmtUSD(quote.bulk_discount_total)}`} brand />
                                    )}
                                    <Row label="Subtotal" value={fmtUSD(quote.subtotal)} />
                                    {quote.coupon_amount > 0 && (
                                        <Row label={`${quote.coupon?.code || "Coupon"}`} value={`− ${fmtUSD(quote.coupon_amount)}`} brand />
                                    )}
                                    <Row
                                        label="Flat Rate Priority Shipping"
                                        value={quote.shipping_fee > 0 ? fmtUSD(quote.shipping_fee) : "FREE"}
                                        brand={quote.shipping_fee === 0}
                                    />
                                    {quote.shipping_fee > 0 && (
                                        <p data-testid="free-ship-hint" className="data-label text-[0.6rem] text-muted">
                                            Add {fmtUSD(200 - quote.subtotal + quote.coupon_amount)} more for free shipping
                                        </p>
                                    )}
                                    {quote.card_processing_fee > 0 && (
                                        <Row
                                            label={`Card Processing Fee (${quote.card_processing_fee_pct || 3}%)`}
                                            value={fmtUSD(quote.card_processing_fee)}
                                        />
                                    )}
                                    <div className="hairline" />
                                    <div className="flex justify-between items-baseline pt-2">
                                        <span className="data-label">Total Due</span>
                                        <span className="font-serif text-3xl text-brand">{fmtUSD(quote.total)}</span>
                                    </div>
                                </div>
                            )}
                            {!cardClientSecret && (
                                <>
                                    <button onClick={submit} disabled={placing} data-testid="place-order-btn" className="btn btn-gold w-full">
                                        {placing ? "Placing…" : "Place Research Order"}
                                    </button>
                                    <p className="data-label text-[0.6rem] text-muted leading-relaxed">
                                        By placing this order you acknowledge that all products are FOR LABORATORY RESEARCH USE ONLY and NOT FOR HUMAN CONSUMPTION.
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {showAuthPrompt && (
                <AuthPromptModal
                    onClose={() => setShowAuthPrompt(false)}
                    onAuthSuccess={async () => {
                        setShowAuthPrompt(false);
                        await placeOrder();
                    }}
                />
            )}
        </div>
    );
}

function Field({ label, value, onChange, className = "", testid, autoComplete, type = "text", inputMode }) {
    return (
        <div className={className}>
            <label className="label">{label}</label>
            <input
                value={value}
                data-testid={testid}
                onChange={(e) => onChange(e.target.value)}
                className="input"
                type={type}
                inputMode={inputMode}
                autoComplete={autoComplete}
            />
        </div>
    );
}

function Row({ label, value, brand }) {
    return (
        <div className="flex justify-between">
            <span className="text-muted">{label}</span>
            <span className={brand ? "text-brand" : "text-primary"}>{value}</span>
        </div>
    );
}

function ConfirmationPanel({ order, config }) {
    const navigate = useNavigate();
    const copy = (txt) => {
        navigator.clipboard.writeText(txt);
        toast.success("Copied to clipboard");
    };

    const inst = {
        zelle: {
            title: "Send via Zelle to:",
            value: `${config.zelle_email}\n${config.zelle_name}`,
            note: `Pay securely via Zelle to ${config.zelle_email} after placing your order. Once your payment is received, we will begin processing and shipping your order promptly.

Important: Please include only your order number (${order.order_number}) in the Zelle notes section. Do not add any additional details.

For any questions, contact us at support@aminoplex.bio`,
        },
        wire: {
            title: "Wire funds to our US business bank:",
            value: [
                `Beneficiary:    ${config.wire_account_name || ""}`,
                `Bank:           ${config.wire_bank_name || ""}`,
                `Bank Address:   ${config.wire_bank_address || ""}`,
                `Account #:      ${config.wire_account_number || ""}`,
                `Routing / ABA:  ${config.wire_routing || ""}`,
                `SWIFT / BIC:    ${config.wire_swift || ""}`,
                `Reference:      Order ${order.order_number}`,
            ].filter((line) => line.split(":")[1]?.trim()).join("\n"),
            note: `Make your payment directly into our bank account above. Please use your Order ID (${order.order_number}) as the payment reference — this is the ONLY reference required. Your order will not be shipped until the funds have cleared in our account (typically 1–2 business days for domestic wires).

For any questions, contact us at support@aminoplex.bio`,
        },
    }[order.payment_method] || {};

    return (
        <div data-testid="order-confirmation" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-3xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-10">
                    <CheckCircle2 size={48} className="text-brand mx-auto mb-6" />
                    <div className="data-label text-brand mb-3">ORDER RECEIVED</div>
                    <h1 className="font-serif text-4xl md:text-5xl mb-4">Thank you.</h1>
                    <p className="text-secondary">Your research order has been recorded with status <span className="text-brand font-mono">PENDING PAYMENT</span>.</p>
                </div>

                <div className="card p-6 mb-6">
                    <div className="flex justify-between items-baseline mb-4">
                        <div className="data-label">ORDER NUMBER</div>
                        <div className="font-mono text-brand text-lg">{order.order_number}</div>
                    </div>
                    <div className="hairline mb-4" />
                    <div className="flex justify-between items-baseline">
                        <span className="data-label">AMOUNT DUE</span>
                        <span className="font-serif text-3xl text-brand">{fmtUSD(order.total)}</span>
                    </div>
                </div>

                <div className="card p-6 mb-6">
                    <div className="data-label text-brand mb-4">PAYMENT INSTRUCTIONS</div>
                    <p className="font-mono text-sm mb-3">{inst.title}</p>
                    <div className="bg-[var(--bg)] border border-[var(--border)] p-4 mb-3 flex items-start justify-between gap-3">
                        <pre className="font-mono text-xs text-primary whitespace-pre-wrap break-all">{inst.value}</pre>
                        <button onClick={() => copy(inst.value || "")} className="btn btn-ghost shrink-0"><Copy size={12} /></button>
                    </div>
                    <p className="data-label text-[0.6rem] leading-relaxed whitespace-pre-line">{inst.note}</p>

                    {order.payment_method === "zelle" && (
                        <div className="mt-6 pt-6 border-t border-[var(--border)] flex flex-col items-center">
                            <div className="data-label mb-3 text-brand">SCAN TO PAY</div>
                            <img
                                src="/zelle-qr.png"
                                alt="Zelle QR · JNA Ventures LLC"
                                className="w-full max-w-xs bg-white p-4 rounded shadow-md"
                                data-testid="zelle-qr-code"
                            />
                            <div className="mt-3 text-center">
                                <div className="font-mono text-xs text-primary">JNA Ventures LLC</div>
                                <div className="font-mono text-[0.65rem] text-muted">Open your bank's Zelle app and scan the code</div>
                            </div>
                        </div>
                    )}
                </div>

                <p className="text-sm text-secondary text-center mb-6">We'll email you once we confirm your payment. Orders ship within 24h of confirmation.</p>

                <div className="flex justify-center gap-3">
                    <button onClick={() => navigate("/account/orders")} className="btn">View My Orders</button>
                    <button onClick={() => navigate("/shop")} className="btn btn-gold">Continue Browsing</button>
                </div>
            </div>
        </div>
    );
}


function AuthPromptModal({ onClose, onAuthSuccess }) {
    const { login, register } = useAuth();
    const navigate = useNavigate();
    const [mode, setMode] = useState("signup"); // "signup" | "signin"
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (mode === "signup") {
                toast.info("Please complete your research-account qualification to place an order.");
                onClose();
                navigate("/register?next=/checkout");
                return;
            } else {
                await login(email.trim(), password);
                toast.success("Signed in — placing your order…");
            }
            await onAuthSuccess();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="auth-prompt-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={onClose}
        >
            <div
                className="bg-[var(--bg-carbon)] border border-[var(--border)] w-full max-w-md rounded-lg shadow-2xl animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
                    <div>
                        <div className="data-label text-brand">ACCOUNT REQUIRED</div>
                        <h3 className="font-serif text-xl text-primary mt-1">Sign up to place your order</h3>
                    </div>
                    <button data-testid="auth-modal-close" onClick={onClose} className="btn btn-ghost p-2">
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 pt-5">
                    <div className="grid grid-cols-2 gap-2 mb-5">
                        <button
                            data-testid="tab-signup"
                            onClick={() => setMode("signup")}
                            className={`btn ${mode === "signup" ? "btn-gold" : "btn-ghost"}`}
                        >
                            <UserPlus size={14} /> Create Account
                        </button>
                        <button
                            data-testid="tab-signin"
                            onClick={() => setMode("signin")}
                            className={`btn ${mode === "signin" ? "btn-gold" : "btn-ghost"}`}
                        >
                            <LogIn size={14} /> Sign In
                        </button>
                    </div>
                </div>

                <form onSubmit={submit} className="px-6 pb-6 space-y-4">
                    {mode === "signup" && (
                        <div>
                            <label className="label">Full Name</label>
                            <input
                                data-testid="auth-modal-name"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="input"
                                autoFocus
                            />
                        </div>
                    )}
                    <div>
                        <label className="label">Email</label>
                        <input
                            data-testid="auth-modal-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input"
                            autoFocus={mode === "signin"}
                        />
                    </div>
                    <div>
                        <label className="label">Password {mode === "signup" && <span className="text-muted">· 6+ chars</span>}</label>
                        <input
                            data-testid="auth-modal-password"
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="auth-modal-submit"
                        className="btn btn-gold w-full"
                    >
                        {busy ? "Working…" : mode === "signup" ? "Create Account & Place Order" : "Sign In & Place Order"}
                    </button>
                    <p className="data-label text-[0.6rem] text-muted text-center">
                        {mode === "signup" ? (
                            <>Already have an account? <button type="button" onClick={() => setMode("signin")} className="text-brand hover:underline">Sign in</button></>
                        ) : (
                            <>New to Aminoplex? <button type="button" onClick={() => setMode("signup")} className="text-brand hover:underline">Create account</button></>
                        )}
                    </p>
                </form>
            </div>
        </div>
    );
}


/* Street-address input with free live autocomplete via Photon (OpenStreetMap).
 * Debounces requests as the user types and shows a dropdown of matching addresses.
 * No API key required.
 */
function StreetAddressField({ value, onChange, onPlaceSelected }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const containerRef = useRef(null);
    const abortRef = useRef(null);

    // Debounced search — fire 250ms after typing stops.
    useEffect(() => {
        if (!value || value.length < 3) { setSuggestions([]); return; }
        const t = setTimeout(async () => {
            if (abortRef.current) abortRef.current.abort();
            const ctl = new AbortController();
            abortRef.current = ctl;
            const results = await searchAddresses(value, { signal: ctl.signal });
            setSuggestions(results);
            setOpen(results.length > 0);
            setActiveIdx(-1);
        }, 250);
        return () => clearTimeout(t);
    }, [value]);

    // Close dropdown on outside click.
    useEffect(() => {
        const onDoc = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const pick = (s) => {
        onPlaceSelected(s);
        setOpen(false);
        setSuggestions([]);
    };

    const onKeyDown = (e) => {
        if (!open || suggestions.length === 0) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % suggestions.length); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length); }
        else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pick(suggestions[activeIdx]); }
        else if (e.key === "Escape") setOpen(false);
    };

    return (
        <div className="sm:col-span-2 relative" ref={containerRef}>
            <label className="label">Street Address</label>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => suggestions.length > 0 && setOpen(true)}
                data-testid="ship-address"
                className="input"
                autoComplete="shipping street-address"
                placeholder="Start typing your address…"
            />
            {open && suggestions.length > 0 && (
                <ul
                    data-testid="ship-address-suggestions"
                    className="absolute z-30 mt-1 w-full bg-[var(--bg-carbon)] border border-[var(--border)] shadow-2xl max-h-72 overflow-y-auto"
                >
                    {suggestions.map((s, i) => (
                        <li
                            key={i}
                            data-testid={`address-suggestion-${i}`}
                            onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                            className={`px-4 py-2.5 text-sm cursor-pointer border-t border-[var(--border)] first:border-t-0 ${
                                i === activeIdx ? "bg-[var(--bg-surface)] text-primary" : "text-secondary hover:bg-[var(--bg-surface)]"
                            }`}
                        >
                            {s.label}
                        </li>
                    ))}
                </ul>
            )}
            <p className="data-label text-[0.55rem] text-muted mt-1">Pick a suggestion to auto-fill city, state & ZIP</p>
        </div>
    );
}
